import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  Attribute,
  AttributeLabel,
  AttributeValue,
} from "@/components/Attribute";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import MyGoogleMap from "@/components/Elements/GoogleMap";
import SocialMediaLinks from "@/components/SocialMediaLinks";
import EventAccordion from "@/components/Core/Events/EventAccordion";
import Weather from "@/components/Weather";
import { getAttributeValue } from "@/utils/utility";
import { db } from "../../utils/firebase-config";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import moment from "moment";
import Script from "next/script";
import Head from "next/head";

const attributes = [
  { label: "Venue", name: "venue_name", sortable: true },
  { label: "Address", name: "address1" },
  { label: "City", name: "venue_city" },
  { label: "Description", name: "description" },
];

const VenueDetailsPage = ({ venue, events, totalItems, internalLink }) => {
  const router = useRouter();
  const currentPath = router.asPath;
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [allEvents, setAllEvents] = useState(events); // Default to future events
  const [localPagination, setLocalPagination] = useState({ skip: 0, take: 20 });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Function to fetch past events
  const fetchPastEvents = async () => {
    setLoading(true);
    const eventsRef = collection(db, "events");

    const pastEventsQuery = query(
      eventsRef,
      where("venue", "==", venue.id),
      orderBy("main_date", "asc")
    );

    const pastEventsSnapshot = await getDocs(pastEventsQuery);
    const pastEvents = pastEventsSnapshot.docs.map((doc) => {
      const eventData = doc.data();
      return {
        id: doc.id,
        ...convertFirestoreData(eventData),
      };
    });

    setAllEvents(pastEvents);
    setLoading(false);
  };

  const handleTogglePastEvents = () => {
    setShowPastEvents(!showPastEvents);
    setCurrentPage(0);
    setLocalPagination({ skip: 0, take: 20 });

    if (!showPastEvents) {
      fetchPastEvents(); // Fetch past events when checkbox is checked
    } else {
      setAllEvents(events);
    }
  };

  const getPaginatedResults = () => {
    return allEvents.slice(
      localPagination.skip,
      localPagination.take + localPagination.skip - 1
    );
  };

  const handleBack = () => {
    router.back();
  };

  return (
    <>
      <Head>
        <title>{`${venue?.venue_name || "Venue"} - ${venue?.venue_city}, ${
          venue?.venue_state
        } - Live Music Events | Webtunes`}</title>
        <meta
          name="description"
          content={`Find upcoming events at ${
            venue?.venue_name || "the venue"
          } on Webtunes.`}
        />
        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content={`${
            venue?.venue_name || "Venue"
          } - Live Music Events | Webtunes`}
        />
        <meta
          property="og:description"
          content={`Explore live music events at ${
            venue?.venue_name || "the venue"
          } on Webtunes.`}
        />
        <meta
          property="og:image"
          content={
            venue?.imageUrl ||
            "https://webtunes.com/images/random/defaultband.jpg"
          }
        />
        <meta
          property="og:url"
          content={`https://webtunes.com/venues/${internalLink}`}
        />
        <meta property="og:type" content="website" />
        {/* Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${
            venue?.venue_name || "Venue"
          } - Live Music Events | Webtunes`}
        />
        <meta
          name="twitter:description"
          content={`Discover live music events happening at ${
            venue?.venue_name || "the venue"
          } on Webtunes.`}
        />
        <meta
          name="twitter:image"
          content={
            venue?.imageUrl ||
            "https://webtunes.com/images/random/defaultband.jpg"
          }
        />
      </Head>

      {/* ✅ Use next/script for JSON-LD Structured Data */}
      <Script
        id="json-ld-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "MusicVenue",
              name: venue?.venue_name || "Venue",
              image:
                venue?.imageUrl ||
                "https://webtunes.com/images/random/defaultband.jpg",
              address: {
                "@type": "PostalAddress",
                streetAddress: venue?.address1,
                addressLocality: venue?.venue_city,
                addressRegion: venue?.venue_state,
                postalCode: venue?.VENUE_ZIP,
                addressCountry: "US",
              },
              sameAs: venue?.socialMediaLinks || [],
              event: events.map((event) => ({
                "@type": "Event",
                name: event.title,
                startDate: event.main_date,
                endDate: event.endDate || undefined,
                eventStatus: "https://schema.org/EventScheduled",
                eventAttendanceMode:
                  "https://schema.org/OfflineEventAttendanceMode",
                location: {
                  "@type": "Place",
                  name: venue?.venue_name || "Venue",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: venue?.address1,
                    addressLocality: venue?.venue_city,
                    addressRegion: venue?.venue_state,
                    postalCode: venue?.VENUE_ZIP,
                    addressCountry: "US",
                  },
                },
                image:
                  event.imageUrl ||
                  "https://webtunes.com/images/random/defaultband.jpg",
                description: event.eventdescription || event.title,
                organizer: {
                  "@type": "Organization",
                  name: venue?.venue_name || "Venue",
                  url:
                    venue?.venue_url ||
                    `https://webtunes.com/venues/${internalLink}`,
                },
                performer:
                  event.bands && event.bands.length > 0
                    ? {
                        "@type": "Person",
                        name:
                          event.bands[0].bandname ||
                          event.title ||
                          "Unknown Performer",
                        url: event.bands[0].internallink
                          ? `https://webtunes.com/bands/${event.bands[0].internallink}`
                          : "https://webtunes.com", // Provide a fallback URL
                      }
                    : {
                        "@type": "Person",
                        name: event.title || "Unknown Performer", // Fallback if no bands
                        url: "https://webtunes.com", // Fallback URL
                      },
                offers: {
                  "@type": "Offer",
                  url: event.ticket_url || "https://webtunes.com",
                  price: "0",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  validFrom: event.validFrom || new Date().toISOString(),
                },
                url: `https://webtunes.com/events?eventId=${event.id}`,
              })),
            },
            null,
            2
          ).replace(/</g, "\\u003c"), // ✅ Fixes hydration error
        }}
      />

      {/* ✅ Use next/script for Google Analytics */}
      <Script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-10NX6JT8S2"
      />
      <Script
        id="google-analytics"
        dangerouslySetInnerHTML={{
          __html: `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-10NX6JT8S2');
    `,
        }}
      />

      <Box
        sx={{
          padding: { xs: "40px 10px 10px 10px", sm: "3", md: "3" },
          margin: "0 auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button onClick={handleBack} variant="contained">
            Back
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: "bold", textAlign: { xs: "center", sm: "left" } }}
          >
            {venue?.venue_name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mb: 5,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { md: "25%" }, width: "100%" }}>
            {venue?.imageUrl && (
              <Box
                component="img"
                src={venue.imageUrl}
                alt="Venue Photo"
                sx={{
                  width: "100%",
                  objectFit: "cover",
                  mb: 3,
                  height: { xs: "180px", sm: "250px" },
                }}
              />
            )}
            <Box>
              {venue?.venue_url && (
                <a
                  href={venue.venue_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {venue.venue_url.includes("facebook.com") ? (
                    <img
                      src="/logos/facebook-48.png"
                      alt="Facebook"
                      height={40}
                    />
                  ) : (
                    <img
                      src="/logos/website-48.png"
                      alt="Website"
                      height={40}
                    />
                  )}
                  Venue Site
                </a>
              )}
              {attributes.map((attribute, idx) => (
                <Attribute key={idx + attribute.name}>
                  <AttributeLabel>{attribute.label}</AttributeLabel>
                  <AttributeValue>
                    {attribute?.component
                      ? getAttributeValue(attribute, venue)
                      : venue[attribute.name]}
                  </AttributeValue>
                </Attribute>
              ))}
              {venue?.socialMediaLinks && (
                <Box sx={{ ml: 1, mt: 1 }}>
                  <SocialMediaLinks
                    size={"48"}
                    links={venue.socialMediaLinks}
                  />
                </Box>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              sx={{ textDecoration: "underline", mb: 2, textAlign: "center" }}
            >
              Map
            </Typography>
            <MyGoogleMap
              markers={[venue]}
              centerProp={{ lng: venue?.lon, lat: venue?.lat }}
            />
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

          <Box sx={{ flex: 2 }}>
            <Weather lat={venue?.lat} lon={venue?.lon} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={showPastEvents}
                  onChange={handleTogglePastEvents}
                />
              }
              label="Show Past Events"
              sx={{ mt: 2 }}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>
              {showPastEvents ? "Past Events" : "Upcoming Events"}
            </Typography>

            <EventAccordion
              data={getPaginatedResults()}
              showIndicators={true}
              totalCount={allEvents.length}
              showCompactDesign={false}
              showImage={true}
              isMainView={false}
              setPaginationProps={(skip, take) =>
                setLocalPagination({ skip, take })
              }
              currentPage={currentPage}
              setCurrentPage={(v) => setCurrentPage(v)}
              from={currentPath}
              type="venue"
            />

            {loading && <Typography>Loading...</Typography>}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export async function getStaticPaths() {
  const venuesRef = collection(db, "venues");
  const querySnapshot = await getDocs(venuesRef);

  const paths = querySnapshot.docs
    .map((doc) => {
      const internallink = doc.data()?.internallink;
      if (internallink) {
        return {
          params: { internalLink: internallink.toLowerCase().replace("/", "") },
        };
      }
      return null;
    })
    .filter(Boolean);
  return {
    paths,
    fallback: "blocking",
  };
}

// Utility function to convert non-serializable Firestore data types
const convertFirestoreData = (data) => {
  if (data === null || data === undefined) {
    return data;
  }

  if (data instanceof Array) {
    return data.map(convertFirestoreData);
  }

  if (data instanceof Object) {
    const newData = {};
    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        const value = data[key];

        // Convert Firestore GeoPoint to lat/lng
        if (
          value &&
          value.latitude !== undefined &&
          value.longitude !== undefined
        ) {
          newData[key] = {
            lat: value.latitude,
            lng: value.longitude,
          };
        } else if (value && value.toDate) {
          newData[key] = value.toDate().toISOString();
        } else {
          newData[key] = convertFirestoreData(value);
        }
      }
    }
    return newData;
  }

  return data;
};

export async function getStaticProps({ params }) {
  const internalLink = params.internalLink;
  const venuesRef = collection(db, "venues");
  const q = query(
    venuesRef,
    where("internallink", "==", `/${params.internalLink.toLowerCase()}/`)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return {
      notFound: true,
    };
  }

  const venueDoc = querySnapshot.docs[0];
  const venueData = venueDoc.data();
  const venue = {
    id: venueDoc.id,
    ...convertFirestoreData(venueData),
  };

  const eventsRef = collection(db, "events");

  //const fourHoursBefore = moment().subtract(4, "hours").toISOString();
  const fourHoursBefore = moment().subtract(4, "hours").toDate();

  const eventsQuery = query(
    eventsRef,
    where("venue", "==", venue.id),
    where("main_date", ">=", fourHoursBefore),
    orderBy("main_date", "asc")
  );

  const eventsSnapshot = await getDocs(eventsQuery);
  const events = eventsSnapshot.docs.map((doc) => {
    const eventData = doc.data();
    return {
      id: doc.id,
      ...convertFirestoreData(eventData),
    };
  });

  return {
    props: {
      venue,
      events,
      internalLink,
      totalItems: events.length,
      //fourHoursBefore,
    },
    revalidate: 60, // Regenerate the page every 60 seconds
  };
}

export default function VenuePage(props) {
  return <VenueDetailsPage {...props} />;
}
